<template>
  <teleport to="body">
    <div class="producto-overlay">
    <div class="producto-modal">
      <!-- Cerrar -->
      <button
        type="button"
        class="producto-modal__close"
        @click="$emit('cerrar')"
      >
        ✕
      </button>

      <!-- Header -->
      <div class="producto-modal__header">
        <h2 class="producto-modal__title">
          {{ producto ? 'Editar producto' : 'Agregar nuevo producto' }}
        </h2>
        <p class="producto-modal__subtitle">
          Define el nombre, categoría y tipo para organizar el inventario.
        </p>
      </div>

      <!-- Formulario -->
      <form
        class="producto-form"
        @submit.prevent="guardarProducto"
      >
        <!-- Nombre -->
        <div class="producto-form__field">
          <label class="producto-form__label">
            Nombre
          </label>
          <input
            v-model="form.nombre"
            type="text"
            class="producto-form__input"
            placeholder="Ej. Paquete de inicio"
            required
          />
        </div>

        <!-- Descripción -->
        <div class="producto-form__field">
          <label class="producto-form__label">
            Descripción
          </label>
          <textarea
            v-model="form.descripcion"
            class="producto-form__textarea"
            rows="3"
            placeholder="Opcional: detalles internos del producto"
          ></textarea>
        </div>

        <!-- Imagen principal -->
        <div class="producto-form__field">
          <label class="producto-form__label">
            Imagen principal
          </label>

          <div class="producto-image-box">
            <div v-if="imagenPreview" class="producto-image-preview">
              <img :src="imagenPreview" alt="Vista previa del producto" />
            </div>
            <div v-else class="producto-image-empty">
              Sin imagen
            </div>

            <div class="producto-image-actions">
              <input
                ref="inputImagenPrincipal"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                class="producto-image-input"
                @change="onImagenSeleccionada"
              />

              <button
                type="button"
                class="producto-form__btn producto-form__btn--ghost"
                @click="$refs.inputImagenPrincipal && $refs.inputImagenPrincipal.click()"
              >
                {{ imagenPreview ? 'Cambiar imagen' : 'Cargar imagen' }}
              </button>

              <button
                v-if="imagenPreview"
                type="button"
                class="producto-form__btn producto-form__btn--ghost-secondary"
                @click="quitarImagen"
              >
                Quitar
              </button>
            </div>
          </div>
        </div>

        <!-- Categoría con buscador -->
        <div class="producto-form__field">
          <label class="producto-form__label">
            Categoría
          </label>
          <div class="producto-form__row producto-form__row--categories">
            <!-- Dropdown custom -->
            <div class="categoria-select">
              <button
                type="button"
                class="categoria-select__control"
                @click="toggleCategoriaDropdown"
              >
                <div
                  class="categoria-select__value"
                  :class="{ 'is-placeholder': !selectedCategoriaLabel }"
                >
                  {{ selectedCategoriaLabel || 'Selecciona una categoría' }}
                </div>
                <span
                  class="categoria-select__arrow"
                  :class="{ 'is-open': categoriaDropdownOpen }"
                ></span>
              </button>

              <transition name="fade-scale">
                <div
                  v-if="categoriaDropdownOpen"
                  class="categoria-select__menu"
                  @click.stop
                >
                  <input
                    ref="categoriaSearchInput"
                    v-model="categoriaSearch"
                    type="text"
                    class="categoria-select__search"
                    placeholder="Buscar categoría…"
                    @click.stop
                  />

                  <div class="categoria-select__list">
                    <button
                      v-for="cat in filteredCategorias"
                      :key="cat.id_categoria"
                      type="button"
                      class="categoria-select__option"
                      :class="{
                        'is-selected': cat.id_categoria === form.id_categoria
                      }"
                      @click="selectCategoria(cat)"
                    >
                      {{ cat.nombre }}
                    </button>

                    <p
                      v-if="filteredCategorias.length === 0"
                      class="categoria-select__empty"
                    >
                      No hay categorías que coincidan.
                    </p>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Botón para crear categorías -->
            <button
              type="button"
              class="producto-form__btn producto-form__btn--ghost"
              @click="mostrarModalCategorias = true"
            >
              + Categoría
            </button>
          </div>
        </div>

        <!-- Tipo de producto -->
        <div class="producto-form__field">
          <label class="producto-form__label">
            Tipo de producto
          </label>
          <select
            v-model="form.tipo_producto"
            class="producto-form__select"
            required
          >
            <option disabled value="">Seleccionar tipo…</option>
            <option value="principal">Principal</option>
            <option value="complemento">Complemento</option>
          </select>
        </div>


        <div class="producto-form__field">
          <div class="producto-form__label-row">
            <label class="producto-form__label">
              Modelos del producto
            </label>
            <button
              type="button"
              class="producto-form__btn producto-form__btn--ghost"
              @click="agregarModelo"
            >
              + Modelo
            </button>
          </div>
          <p class="producto-form__hint">
            Opcional. Si este producto maneja variantes, registra aquí cada combinación de nombre y modelo.
          </p>

          <div v-if="!form.modelos.length" class="producto-modelos-empty">
            Sin modelos registrados.
          </div>

          <div v-else class="producto-modelos-list">
            <div
              v-for="(modelo, index) in form.modelos"
              :key="modelo.uid || modelo.id_modelo_producto || index"
              class="producto-modelo-item"
            >
              <div class="producto-modelo-item__grid">
                <div class="producto-form__field">
                  <label class="producto-form__label">Nombre</label>
                  <input
                    v-model="modelo.nombre"
                    type="text"
                    class="producto-form__input"
                    placeholder="Ej. Blanco"
                  />
                </div>

                <div class="producto-form__field">
                  <label class="producto-form__label">Modelo</label>
                  <input
                    v-model="modelo.modelo"
                    type="text"
                    class="producto-form__input"
                    placeholder="Ej. BLK-2026"
                  />
                </div>
              </div>

              <div class="producto-modelo-item__actions">
                <button
                  type="button"
                  class="producto-form__btn producto-form__btn--ghost-secondary"
                  @click="eliminarModelo(index)"
                >
                  Quitar
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Error global -->
        <p
          v-if="error"
          class="producto-form__error"
        >
          {{ error }}
        </p>

        <!-- Footer -->
        <div class="producto-form__footer">
          <button
            type="button"
            class="producto-form__btn producto-form__btn--ghost-secondary"
            @click="$emit('cerrar')"
          >
            Cancelar
          </button>
          <button
            type="submit"
            class="producto-form__btn producto-form__btn--primary"
          >
            Guardar
          </button>
        </div>
      </form>
    </div>

    <!-- Modal de gestión de categorías -->
    <GestionCategorias
      v-if="mostrarModalCategorias"
      @cerrar="mostrarModalCategorias = false"
      @finalizarCreacionCategoria="handleCategoriaCreada"
    />
    </div>
  </teleport>
</template>
<script>
import axios from 'axios';
import GestionCategorias from '@/components/almacen/categorias/GestionCategorias.vue';

export default {
  name: 'FormularioProducto',
  components: {
    GestionCategorias,
  },
  props: {
    producto: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: {
        nombre: '',
        descripcion: '',
        id_categoria: '',
        tipo_producto: '',
        imagen_principal_base64: '',
        imagen_principal_nombre: '',
        quitar_imagen_principal: false,
        modelos: [],
      },
      imagenPreview: '',
      categorias: [],
      mostrarModalCategorias: false,
      error: '',
      categoriaDropdownOpen: false,
      categoriaSearch: '',
    };
  },
  computed: {
    selectedCategoriaLabel() {
      if (!this.form.id_categoria || !Array.isArray(this.categorias)) {
        return '';
      }
      const found = this.categorias.find(
        (c) => String(c.id_categoria) === String(this.form.id_categoria)
      );
      return found ? found.nombre : '';
    },
    filteredCategorias() {
      if (!Array.isArray(this.categorias)) return [];
      const term = this.categoriaSearch.trim().toLowerCase();
      if (!term) return this.categorias;
      return this.categorias.filter((c) =>
        String(c.nombre || '').toLowerCase().includes(term)
      );
    },
  },
  mounted() {
    if (this.producto) {
      this.form = {
        nombre: this.producto.nombre || '',
        descripcion: this.producto.descripcion || '',
        id_categoria: this.producto.id_categoria || '',
        tipo_producto: this.producto.tipo_producto || '',
        id_producto: this.producto.id_producto,
        token_producto: this.producto.token_producto,
        imagen_principal_base64: '',
        imagen_principal_nombre: '',
        quitar_imagen_principal: false,
        modelos: (this.producto.modelos || []).map((modelo, index) => ({
          id_modelo_producto: modelo.id_modelo_producto || null,
          nombre: modelo.nombre || '',
          modelo: modelo.modelo || '',
          uid: 'modelo-existente-' + (modelo.id_modelo_producto || index),
        })),
      };
      this.imagenPreview =
        this.producto.imagen_principal_url ||
        this.producto.imagen_principal ||
        '';
    }
    this.cargarCategorias();
  },
  methods: {
    async archivoABase64(file) {
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    async onImagenSeleccionada(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;

      try {
        this.form.imagen_principal_base64 = await this.archivoABase64(file);
        this.form.imagen_principal_nombre = file.name || 'producto';
        this.form.quitar_imagen_principal = false;
        this.imagenPreview = this.form.imagen_principal_base64;
      } catch (error) {
        console.error('Error al leer la imagen del producto:', error);
        this.$toast?.error('No se pudo cargar la imagen seleccionada.');
      }
    },

    quitarImagen() {
      this.form.imagen_principal_base64 = '';
      this.form.imagen_principal_nombre = '';
      this.form.quitar_imagen_principal = true;
      this.imagenPreview = '';
      if (this.$refs.inputImagenPrincipal) {
        this.$refs.inputImagenPrincipal.value = '';
      }
    },

    agregarModelo() {
      this.form.modelos.push({
        id_modelo_producto: null,
        nombre: '',
        modelo: '',
        uid: 'modelo-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
      });
    },

    eliminarModelo(index) {
      this.form.modelos.splice(index, 1);
    },

    normalizarModelos() {
      return (this.form.modelos || [])
        .map((modelo) => ({
          id_modelo_producto: modelo.id_modelo_producto || null,
          nombre: String(modelo.nombre || '').trim(),
          modelo: String(modelo.modelo || '').trim(),
        }))
        .filter((modelo) => modelo.nombre || modelo.modelo);
    },

    async guardarProducto() {
      this.error = '';

      // Validaciones de formulario (se quedan sin toast)
      const nombreLimpio = (this.form.nombre || '').trim();
      if (!nombreLimpio) {
        this.error = 'El nombre del producto es obligatorio.';
        return;
      }
      if (!this.form.id_categoria) {
        this.error = 'Selecciona una categoría para el producto.';
        return;
      }
      if (!this.form.tipo_producto) {
        this.error =
          'Selecciona el tipo de producto (principal o complemento).';
        return;
      }

      const modelosNormalizados = this.normalizarModelos();
      const modelosInvalidos = modelosNormalizados.some((modelo) => !modelo.nombre || !modelo.modelo);
      if (modelosInvalidos) {
        this.error = 'Cada modelo debe tener nombre y modelo.';
        return;
      }

      this.form.modelos = this.normalizarModelos();

      try {
        let response;

        if (this.producto) {
          // Actualizar producto existente
          response = await axios.post(
            '/api/almacen/productos/actualizar',
            this.form
          );
        } else {
          // Crear nuevo producto
          const token = Math.random()
            .toString(36)
            .substring(2, 10)
            .toUpperCase();
          this.form.token_producto = token;

          response = await axios.post(
            '/api/almacen/productos/crear',
            this.form
          );
        }

        if (response.data?.status === 'ok') {
          const id =
            response.data.id_producto ||
            (this.producto && this.producto.id_producto);
          const esPrincipal = this.form.tipo_producto === 'principal';

          this.error = '';
          // Toast de éxito (el modal se cerrará desde el padre con @guardado)
          const mensajeExito = this.producto
            ? 'Producto actualizado correctamente.'
            : 'Producto creado correctamente.';
          this.$toast?.success(mensajeExito);

          this.$emit('guardado', id, esPrincipal);
        } else {
          const mensajeError =
            response.data?.message || 'No se pudo guardar el producto.';
          this.error = mensajeError;
          // Toast de error, sin cerrar modal
          this.$toast?.error(mensajeError);
        }
      } catch (error) {
        console.error('Error al guardar producto:', error);
        const mensajeError = 'Ocurrió un error al guardar el producto.';
        this.error = mensajeError;
        // Toast de error, sin cerrar modal
        this.$toast?.error(mensajeError);
      }
    },

    async cargarCategorias() {
      try {
        const res = await axios.get('/api/almacen/categorias/listar');
        this.categorias = Array.isArray(res.data) ? res.data : [];
      } catch (error) {
        console.error('Error al cargar categorías', error);
        // Feedback al usuario si falla la carga de categorías
        this.$toast?.error('No se pudieron cargar las categorías.');
      }
    },

    handleCategoriaCreada() {
      this.cargarCategorias();
      this.mostrarModalCategorias = false;
    },

    toggleCategoriaDropdown() {
      this.categoriaDropdownOpen = !this.categoriaDropdownOpen;
      if (this.categoriaDropdownOpen) {
        this.categoriaSearch = '';
        this.$nextTick(() => {
          if (this.$refs.categoriaSearchInput) {
            this.$refs.categoriaSearchInput.focus();
          }
        });
      }
    },

    selectCategoria(cat) {
      this.form.id_categoria = cat.id_categoria;
      this.categoriaDropdownOpen = false;
    },
  },
};
</script>


<style scoped lang="scss">
.producto-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  overflow-y: auto;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.3),
      rgba(15, 23, 42, 0.98)
    );
  backdrop-filter: blur(18px);
}

/* Tarjeta principal */
.producto-modal {
  position: relative;
  width: 100%;
  max-width: 640px;
  max-height: 85vh;
  border-radius: 1.6rem;
  padding: 1.4rem 1.35rem 1.25rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.35),
      rgba(15, 23, 42, 0.97)
    );
  border: 1px solid rgba(148, 163, 184, 0.7);
  box-shadow:
    0 22px 60px rgba(15, 23, 42, 0.95),
    0 0 0 1px rgba(15, 23, 42, 0.9);
  color: #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Cerrar */
.producto-modal__close {
  position: absolute;
  top: 0.6rem;
  right: 0.8rem;
  border: none;
  background: transparent;
  color: #cbd5f5;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.2rem;
}

/* Header */
.producto-modal__header {
  margin-bottom: 0.9rem;
}

.producto-modal__title {
  font-size: 1rem;
  font-weight: 600;
  color: #f9fafb;
}

.producto-modal__subtitle {
  margin-top: 0.2rem;
  font-size: 0.8rem;
  color: #94a3b8;
}

/* Form */
.producto-form {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  overflow-y: auto;
  min-height: 0;
  padding-right: 0.25rem;
}

.producto-form::-webkit-scrollbar {
  width: 8px;
}

.producto-form::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.35);
  border-radius: 999px;
}

.producto-form::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.65);
  border-radius: 999px;
}

.producto-form__field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.producto-form__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.producto-form__hint {
  margin-top: -0.1rem;
  font-size: 0.75rem;
  color: #94a3b8;
}

.producto-modelos-empty {
  border-radius: 1rem;
  border: 1px dashed rgba(148, 163, 184, 0.45);
  background: rgba(15, 23, 42, 0.4);
  padding: 0.8rem 0.9rem;
  font-size: 0.78rem;
  color: #cbd5f5;
}

.producto-modelos-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.producto-modelo-item {
  border-radius: 1.1rem;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.5);
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.producto-modelo-item__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.7rem;
}

.producto-modelo-item__actions {
  display: flex;
  justify-content: flex-end;
}

.producto-form__label {
  font-size: 0.8rem;
  color: #cbd5f5;
}

/* Inputs / textarea / select */
.producto-form__input,
.producto-form__textarea,
.producto-form__select {
  width: 100%;
  border-radius: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.9);
  padding: 0.45rem 0.75rem;
  font-size: 0.85rem;
  color: #e5e7eb;
  outline: none;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.9);
  transition:
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    background 0.16s ease;
}

.producto-form__textarea {
  resize: vertical;
  min-height: 60px;
  max-height: 160px;
}

.producto-form__input::placeholder,
.producto-form__textarea::placeholder {
  color: #6b7280;
}

.producto-form__select {
  appearance: none;
  background-image: linear-gradient(
    45deg,
    transparent 50%,
    rgba(148, 163, 184, 0.9) 50%
  );
  background-position: calc(100% - 12px) center;
  background-repeat: no-repeat;
  background-size: 8px 8px;
}

.producto-form__input:focus,
.producto-form__textarea:focus,
.producto-form__select:focus {
  border-color: rgba(129, 140, 248, 0.95);
  background: rgba(15, 23, 42, 0.98);
  box-shadow:
    0 0 0 1px rgba(129, 140, 248, 0.7),
    0 0 0 1px rgba(15, 23, 42, 1);
}

/* Fila categoría */
.producto-form__row {
  display: flex;
  gap: 0.45rem;
  align-items: center;
}

.producto-form__row--categories {
  align-items: stretch;
}

/* Error global */
.producto-form__error {
  font-size: 0.78rem;
  color: #fca5a5;
  margin-top: 0.1rem;
}

/* Footer */
.producto-form__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.45rem;
  margin-top: 0.4rem;
}

/* Botones */
.producto-form__btn {
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 0.8rem;
  padding: 0.35rem 0.95rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 90px;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.18s ease,
    transform 0.08s ease;
}

.producto-form__btn--ghost {
  background: rgba(15, 23, 42, 0.9);
  border-color: rgba(148, 163, 184, 0.7);
  color: #e5e7eb;
  white-space: nowrap;
}

.producto-form__btn--ghost-secondary {
  background: rgba(15, 23, 42, 0.85);
  border-color: rgba(148, 163, 184, 0.6);
  color: #e5e7eb;
}

.producto-form__btn--ghost:hover,
.producto-form__btn--ghost-secondary:hover {
  background: rgba(15, 23, 42, 1);
  border-color: rgba(148, 163, 184, 0.95);
}

.producto-form__btn--primary {
  background: #e5e7eb;
  color: #020617;
  border-color: transparent;
}

.producto-form__btn--primary:hover {
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.9);
}

/* Dropdown categorías */
.categoria-select {
  position: relative;
  flex: 1;
}

.categoria-select__control {
  width: 100%;
  border-radius: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.9);
  padding: 0.4rem 0.7rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  cursor: pointer;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.9);
  transition:
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    background 0.16s ease;
}

.categoria-select__control:hover {
  border-color: rgba(148, 163, 184, 0.95);
}

.categoria-select__value {
  font-size: 0.82rem;
  color: #e5e7eb;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.categoria-select__value.is-placeholder {
  color: #6b7280;
}

.categoria-select__arrow {
  width: 8px;
  height: 8px;
  border-right: 1.5px solid #cbd5f5;
  border-bottom: 1.5px solid #cbd5f5;
  transform: rotate(45deg) translateY(-1px);
  transition: transform 0.16s ease;
  flex-shrink: 0;
}

.categoria-select__arrow.is-open {
  transform: rotate(-135deg) translateY(1px);
}

/* Menú del dropdown */
.categoria-select__menu {
  position: absolute;
  inset: calc(100% + 4px) 0 auto 0;
  border-radius: 1rem;
  background: rgba(15, 23, 42, 0.97);
  border: 1px solid rgba(148, 163, 184, 0.8);
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.98);
  padding: 0.45rem 0.45rem 0.4rem;
  z-index: 10;
}

/* Buscador interno */
.categoria-select__search {
  width: 100%;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.95);
  padding: 0.3rem 0.6rem;
  font-size: 0.78rem;
  color: #e5e7eb;
  outline: none;
  margin-bottom: 0.35rem;
}

.categoria-select__search::placeholder {
  color: #6b7280;
}

/* Lista con scroll */
.categoria-select__list {
  max-height: 210px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.7) transparent;
}

/* WebKit scrollbars */
.categoria-select__list::-webkit-scrollbar {
  width: 4px;
}

.categoria-select__list::-webkit-scrollbar-track {
  background: transparent;
}

.categoria-select__list::-webkit-scrollbar-thumb {
  background: linear-gradient(
    180deg,
    rgba(148, 163, 184, 0.9),
    rgba(129, 140, 248, 0.9)
  );
  border-radius: 999px;
}

/* Opción */
.categoria-select__option {
  border-radius: 0.8rem;
  border: 1px solid transparent;
  background: transparent;
  color: #e5e7eb;
  font-size: 0.8rem;
  padding: 0.3rem 0.5rem;
  text-align: left;
  cursor: pointer;
  transition:
    background 0.14s ease,
    border-color 0.14s ease;
}

.categoria-select__option:hover {
  background: rgba(30, 64, 175, 0.35);
  border-color: rgba(129, 140, 248, 0.9);
}

.categoria-select__option.is-selected {
  background: rgba(30, 64, 175, 0.6);
  border-color: rgba(129, 140, 248, 1);
}

/* Estado vacío */
.categoria-select__empty {
  font-size: 0.78rem;
  color: #9ca3af;
  font-style: italic;
  padding: 0.25rem 0.3rem;
}

/* Animación */
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition:
    opacity 0.12s ease,
    transform 0.12s ease;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

.producto-image-box {
  display: flex;
  gap: 0.9rem;
  align-items: center;
  flex-wrap: wrap;
}

.producto-image-preview,
.producto-image-empty {
  width: 88px;
  height: 88px;
  border-radius: 1rem;
  border: 1px dashed rgba(148, 163, 184, 0.6);
  background: rgba(15, 23, 42, 0.88);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 0.76rem;
}

.producto-image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.producto-image-actions {
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
}

.producto-image-input {
  display: none;
}

/* Responsive */
@media (max-width: 640px) {
  .producto-modal {
    padding: 1.1rem 1rem 1rem;
    border-radius: 1.3rem;
  }

  .producto-form__row {
    flex-direction: column;
    align-items: stretch;
  }

  .producto-form__btn--ghost {
    align-self: flex-start;
  }
}
</style>
